import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedListenableSubscribable } from '@benzinga/subscribable';

import {
  //EarningReport,
  Financials,
  FinancialsQuery,
  //OperationRatio,
  Ownership,
  Peer,
  //ValuationRatio,
  ValuationRatiosQuery,
} from './entities';
import { SecuritiesRestful, SecuritiesTransport } from './restful';
import {
  ingestInsiders,
  ingestOwnership,
  ingestPeers,
  ingestSecurities,
  ingestOperationRatios,
  ingestValuationRatios,
  ingestEarningsReports,
} from './ingest';
import { Session } from '@benzinga/session';
import { InsidersFiling } from '@benzinga/insider-trades-manager';

/**
 * Event emitted when there was error fetching financials
 *
 * @event
 *
 * @interface ErrorEvent
 */
interface ErrorEvent {
  error?: SafeError;
  errorType:
    | 'get_fundamentals_error'
    | 'get_financials_error'
    | 'get_ownership_error'
    | 'get_peers_error'
    | 'get_insiders_error'
    | 'get_valuation_ratios_error'
    | 'get_operation_ratios_error'
    | 'get_earning_reports_error';
  type: 'error';
}

/**
 * Financials update or receival event
 *
 * @event
 *
 * @interface FinancialsUpdateEvent
 */

// TODO: These seem to be incorrectly typed

interface FinancialsUpdateEvent {
  //valuationRatios?: ValuationRatio[];
  valuationRatios?: Financials[];
  financials?: Financials[];
  fundamentals?: Financials[];
  operationRatios?: Financials[];
  //operationRatios?: OperationRatio[];
  //earningReports?: EarningReport[];
  earningReports?: Financials[];
  type:
    | 'financials_received'
    | 'fundamentals_received'
    | 'valuation_ratios_received'
    | 'operation_ratios_received'
    | 'earning_reports_received';
}

export type FinancialsRequestEvent =
  | ErrorEvent
  | FinancialsUpdateEvent
  | OwnershipUpdateEvent
  | PeersUpdateEvent
  | InsidersUpdateEvent;

interface OwnershipUpdateEvent {
  ownership: Ownership;
  type: 'ownership_received' | 'ownership_updated';
}

interface PeersUpdateEvent {
  peers: Peer[];
  type: 'peers_received' | 'peers_updated';
}

interface InsidersUpdateEvent {
  insiders: InsidersFiling[];
  type: 'insiders_received' | 'insiders_updated';
}
interface SecuritiesFunctions {
  getFinancials: SecuritiesRequest['getFinancials'];
  getFundamentals: SecuritiesRequest['getFundamentals'];
}

export class SecuritiesRequest extends ExtendedListenableSubscribable<FinancialsRequestEvent, SecuritiesFunctions> {
  private restful: SecuritiesTransport;

  constructor(session: Session) {
    super();
    this.restful = new SecuritiesRestful(session);
  }

  public getEarningReports = async (query: ValuationRatiosQuery): SafePromise<Financials[]> => {
    const earningReports = await ingestEarningsReports(this.restful.getEarningReports(query));
    if (earningReports.err) {
      this.dispatch({
        error: earningReports.err,
        errorType: 'get_earning_reports_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        earningReports: earningReports.ok,
        type: 'earning_reports_received',
      });
    }
    return earningReports;
  };

  public getOperationRatios = async (query: ValuationRatiosQuery): SafePromise<Financials[]> => {
    const operationRatios = await ingestOperationRatios(this.restful.getOperationRatios(query));
    if (operationRatios.err) {
      this.dispatch({
        error: operationRatios.err,
        errorType: 'get_operation_ratios_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        operationRatios: operationRatios.ok,
        type: 'operation_ratios_received',
      });
    }
    return operationRatios;
  };

  public getValuationRatios = async (query: ValuationRatiosQuery): SafePromise<Financials[]> => {
    const valuationRatios = await ingestValuationRatios(this.restful.getValuationRatios(query));
    if (valuationRatios.err) {
      this.dispatch({
        error: valuationRatios.err,
        errorType: 'get_valuation_ratios_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        type: 'valuation_ratios_received',
        valuationRatios: valuationRatios.ok,
      });
    }
    return valuationRatios;
  };

  public getFinancials = async (query: FinancialsQuery): SafePromise<Financials[]> => {
    const financials = await ingestSecurities(this.restful.getFinancials(query));
    if (financials.err) {
      this.dispatch({
        error: financials.err,
        errorType: 'get_financials_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        financials: financials.ok,
        type: 'financials_received',
      });
    }
    return financials;
  };

  public getFundamentals = async (query: FinancialsQuery): SafePromise<Financials[]> => {
    const fundamentals = await ingestSecurities(this.restful.getFundamentals(query));
    if (fundamentals.err) {
      this.dispatch({
        error: fundamentals.err,
        errorType: 'get_fundamentals_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        fundamentals: fundamentals.ok,
        type: 'fundamentals_received',
      });
    }
    return fundamentals;
  };

  public getOwnership = async (symbol: string): SafePromise<Ownership> => {
    const ownership = await ingestOwnership(this.restful.getOwnership(symbol));
    if (ownership.err) {
      this.dispatch({
        error: ownership.err,
        errorType: 'get_ownership_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        ownership: ownership.ok,
        type: 'ownership_received',
      });
    }
    return ownership;
  };

  /** @deprecated Use ScannerManager.getRelatedSymbols or ScannerManager.getRelatedSymbolPeerQuotes instead. */
  public getPeers = async (symbol: string, fields: string[]): SafePromise<Peer[]> => {
    const peers = await ingestPeers(this.restful.getPeers(symbol, fields));
    if (peers.err) {
      this.dispatch({
        error: peers.err,
        errorType: 'get_peers_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        peers: peers.ok,
        type: 'peers_received',
      });
    }
    return peers;
  };

  public getInsiders = async (symbol: string): SafePromise<InsidersFiling[]> => {
    const insiders = await ingestInsiders(this.restful.getInsiders(symbol));
    if (insiders.err) {
      this.dispatch({
        error: insiders.err,
        errorType: 'get_insiders_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        insiders: insiders.ok,
        type: 'insiders_received',
      });
    }
    return insiders;
  };

  protected onSubscribe(): SecuritiesFunctions {
    return {
      getFinancials: this.getFinancials,
      getFundamentals: this.getFundamentals,
    };
  }
}
